/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React from "react";
import { laToday } from "../tm/tmConst";
import { CalendarRange } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const lastDay = (y, m) => new Date(y, m, 0).getDate();
export const EMPTY_PERIOD = { mode: "all", year: "", quarter: "", month: "", start: "", end: "" };

/* {mode, year, quarter, month, start, end} → {start, end} (YYYY-MM-DD, 含端点)；all → {} */
export function periodRange(p) {
  const y = Number(p.year) || Number(laToday().slice(0, 4));
  if (p.mode === "year") return { start: `${y}-01-01`, end: `${y}-12-31` };
  if (p.mode === "quarter") { const q = Number(p.quarter) || 1; const m1 = (q - 1) * 3 + 1; return { start: `${y}-${pad(m1)}-01`, end: `${y}-${pad(m1 + 2)}-${lastDay(y, m1 + 2)}` }; }
  if (p.mode === "month" && p.month) { const [yy, mm] = p.month.split("-").map(Number); return { start: `${p.month}-01`, end: `${p.month}-${lastDay(yy, mm)}` }; }
  if (p.mode === "custom") return { ...(p.start ? { start: p.start } : {}), ...(p.end ? { end: p.end } : {}) };
  return {};
}

export function periodLabel(p, zh) {
  const r = periodRange(p);
  if (p.mode === "all" || (!r.start && !r.end)) return zh ? "全部时间" : "All time";
  if (p.mode === "year") return zh ? `${p.year || laToday().slice(0, 4)} 年` : `${p.year || laToday().slice(0, 4)}`;
  if (p.mode === "quarter") return zh ? `${p.year || laToday().slice(0, 4)} 年 Q${p.quarter || 1}` : `Q${p.quarter || 1} ${p.year || laToday().slice(0, 4)}`;
  if (p.mode === "month") return p.month || "";
  return `${r.start || "…"} ~ ${r.end || "…"}`;
}

/* 财务时间筛选：快捷（全部 / 本月 / 本季度 / 本年 / 去年）+ 年 / 季度 / 月 / 自定义区间 */
export function PeriodFilter({ value, onChange, zh, testId = "period" }) {
  const today = laToday();
  const ty = Number(today.slice(0, 4)), tm = Number(today.slice(5, 7));
  const tq = Math.floor((tm - 1) / 3) + 1;
  const years = Array.from({ length: 6 }, (_, i) => String(ty - i));
  const set = (patch) => onChange({ ...value, ...patch });
  const quick = [
    ["all", zh ? "全部" : "All", { ...EMPTY_PERIOD }],
    ["thisMonth", zh ? "本月" : "This month", { mode: "month", month: today.slice(0, 7) }],
    ["thisQuarter", zh ? "本季度" : "This quarter", { mode: "quarter", year: String(ty), quarter: String(tq) }],
    ["thisYear", zh ? "本年" : "This year", { mode: "year", year: String(ty) }],
    ["lastYear", zh ? "去年" : "Last year", { mode: "year", year: String(ty - 1) }],
  ];
  const isQuick = (q) => { const r1 = periodRange({ ...EMPTY_PERIOD, ...q[2] }); const r2 = periodRange(value); return (r1.start || "") === (r2.start || "") && (r1.end || "") === (r2.end || ""); };
  const sel = "text-sm rounded-lg border border-slate-200 px-2 py-1.5 bg-white";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testId}-filter`}>
      <CalendarRange className="h-4 w-4 text-slate-400" />
      <div className="inline-flex rounded-lg bg-slate-100 p-0.5" data-testid={`${testId}-quick`}>
        {quick.map((q) => <button key={q[0]} type="button" data-testid={`${testId}-quick-${q[0]}`} aria-pressed={isQuick(q)} onClick={() => onChange({ ...EMPTY_PERIOD, ...q[2] })} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${isQuick(q) ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>{q[1]}</button>)}
      </div>
      <select data-testid={`${testId}-mode`} className={sel} value={value.mode} onChange={(e) => { const m = e.target.value; set({ mode: m, year: value.year || String(ty), quarter: value.quarter || String(tq), month: value.month || today.slice(0, 7) }); }}>
        <option value="all">{zh ? "不限时间" : "Any time"}</option>
        <option value="year">{zh ? "按年" : "Year"}</option>
        <option value="quarter">{zh ? "按季度" : "Quarter"}</option>
        <option value="month">{zh ? "按月" : "Month"}</option>
        <option value="custom">{zh ? "自定义区间" : "Custom range"}</option>
      </select>
      {(value.mode === "year" || value.mode === "quarter") && (
        <select data-testid={`${testId}-year`} className={sel} value={value.year || String(ty)} onChange={(e) => set({ year: e.target.value })}>{years.map((y) => <option key={y} value={y}>{zh ? `${y} 年` : y}</option>)}</select>
      )}
      {value.mode === "quarter" && (
        <select data-testid={`${testId}-quarter`} className={sel} value={value.quarter || String(tq)} onChange={(e) => set({ quarter: e.target.value })}>{[1, 2, 3, 4].map((q) => <option key={q} value={String(q)}>{`Q${q} (${(q - 1) * 3 + 1}-${q * 3}${zh ? "月" : ""})`}</option>)}</select>
      )}
      {value.mode === "month" && <input type="month" data-testid={`${testId}-month`} className={sel} value={value.month || today.slice(0, 7)} max={today.slice(0, 7)} onChange={(e) => set({ month: e.target.value })} />}
      {value.mode === "custom" && (
        <>
          <input type="date" data-testid={`${testId}-start`} className={sel} value={value.start} max={value.end || undefined} onChange={(e) => set({ start: e.target.value })} />
          <span className="text-slate-400 text-xs">~</span>
          <input type="date" data-testid={`${testId}-end`} className={sel} value={value.end} min={value.start || undefined} onChange={(e) => set({ end: e.target.value })} />
        </>
      )}
      <span className="text-xs font-semibold text-brand-600 bg-brand-50 px-2 py-1 rounded-full" data-testid={`${testId}-label`}>{periodLabel(value, zh)}</span>
    </div>
  );
}
